import {
  CANVAS_ELEMENT_TYPES,
  CANVAS_TOOLS,
  isBoxElement,
} from "../nativeCanvasElements";
import { getArrowPoints, getElementBounds } from "../geometry/elementBounds";
import { DEFAULT_HIT_PADDING, getElementAtPoint } from "../geometry/elementHitTest";
import {
  getArrowEndpointAtPoint,
  getResizeCursor,
  getResizeHandleAtPoint,
  getScreenBounds,
} from "../geometry/selectionHandles";
import { findBindTarget } from "../geometry/arrowBinding";

export const SESSION_TYPES = Object.freeze({
  ARROW_ENDPOINT: "arrowEndpoint",
  CREATE: "create",
  MARQUEE: "marquee",
  MOVE: "move",
  PAN: "pan",
  RESIZE: "resize",
});

/**
 * @param {object} state
 * @returns {number} hit padding in scene units for the current zoom
 */
export const getHitPadding = (state) =>
  DEFAULT_HIT_PADDING / state.viewport.zoom;

const getSingleSelectedElement = (state) => {
  if (state.selectedIds.size !== 1) return null;
  const [selectedId] = state.selectedIds;

  return state.elements.find((element) => element.id === selectedId) ?? null;
};

const findArrowEndpointSession = ({ element, screenPoint, state }) => {
  const endpoint = getArrowEndpointAtPoint({
    arrowPoints: getArrowPoints(element),
    screenPoint,
    viewport: state.viewport,
  });

  return endpoint
    ? { endpoint, original: element, type: SESSION_TYPES.ARROW_ENDPOINT }
    : null;
};

const findResizeSession = ({ element, screenPoint, state }) => {
  const handle = getResizeHandleAtPoint({
    screenBounds: getScreenBounds({
      bounds: getElementBounds(element),
      viewport: state.viewport,
    }),
    screenPoint,
  });

  return handle
    ? { handle, original: element, type: SESSION_TYPES.RESIZE }
    : null;
};

/**
 * Resize or arrow-endpoint session when the pointer is over a selection
 * handle of the single selected element.
 * @param {{ screenPoint: { x: number, y: number }, state: object }} params
 * @returns {object | null}
 */
export const findHandleSession = ({ screenPoint, state }) => {
  const element = getSingleSelectedElement(state);
  if (!element) return null;
  if (element.type === CANVAS_ELEMENT_TYPES.ARROW) {
    return findArrowEndpointSession({ element, screenPoint, state });
  }
  if (!isBoxElement(element)) return null;

  return findResizeSession({ element, screenPoint, state });
};

/**
 * Cursor + bind-target highlight for idle pointer movement.
 * @param {{ payload: object, state: object }} params
 * @returns {{ bindTargetId: string | null, cursor: string | null }}
 */
export const getHoverFeedback = ({ payload, state }) => {
  if (state.tool === CANVAS_TOOLS.ARROW) {
    const bindTarget = findBindTarget({
      elements: state.elements,
      point: payload.point,
    });
    return { bindTargetId: bindTarget?.id ?? null, cursor: "crosshair" };
  }
  if (state.tool !== CANVAS_TOOLS.SELECT) return { bindTargetId: null, cursor: null };

  const handleSession = findHandleSession({
    screenPoint: payload.screenPoint,
    state,
  });
  if (handleSession) {
    const cursor =
      handleSession.type === SESSION_TYPES.RESIZE
        ? getResizeCursor(handleSession.handle)
        : "pointer";
    return { bindTargetId: null, cursor };
  }
  const element = getElementAtPoint({
    elements: state.elements,
    padding: getHitPadding(state),
    point: payload.point,
  });

  return { bindTargetId: null, cursor: element ? "move" : "default" };
};
